import React, { ReactNode, useContext, useState } from "react";
import { Button, MCard } from "components/Shared";
import TextField from "@mui/material/TextField";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";
import IconButton from "components/Shared/IconButton";
import { classes } from "components/Utility";
import { deepCopy } from "./helpers";
import { setObjectValue } from "components/Shared/Functions";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { showExpressionEditor } from "./CodeEditor/ExpressionEditor";
import MSelect, { MSelectOption } from "components/Shared/MSelect";
import { Typography } from "@mui/material";

export interface EnumerationEditorField<T> {
  label: string,
  property: keyof T,
  required?: boolean,
  isExpression?: boolean,
  blockCommas?: boolean,
  hideOnAdd?: boolean,
  options?: MSelectOption[],
};

interface Props<T> {
  title?: string,
  items: T[],
  fields: EnumerationEditorField<T>[],
  bulkEditFields?: (keyof T)[],
  onChange: (items: T[]) => void,
  displayItem: (item: T) => ReactNode,
  preAddLogic?: (item: T) => void,
  noCard?: boolean,
  stackEditDelete?: boolean,
};

const reorderHandleClassname = "reorder-handle";

const FormEnumerationEditor = <T,>({ title, items, fields, bulkEditFields, onChange, displayItem, noCard, preAddLogic, stackEditDelete }: Props<T>) => {
  const getEmptyItem = () => {
    let item = {} as T;
    fields.forEach(f => setObjectValue(item, f.property, ""));
    return item;
  };
  
  const [newItem, setNewItem] = useState<T>(getEmptyItem);
  const [editItem, setEditItem] = useState<T>(null);
  const [listEditIndex, setListEditIndex] = useState<number>();

  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkEditValue, setBulkEditValue] = useState("");

  const startEditListItem = (index: number) => {
    setEditItem(deepCopy(items[index]));
    setListEditIndex(index);
  };

  const stopEditListItem = (save: boolean) => {
    if (save) {
      let newItems = deepCopy(items);
      newItems[listEditIndex] = editItem;
      onChange(newItems);
    }

    setListEditIndex(null);
    setEditItem(null);
  };

  const addNewListItem = () => {
    let newItems = deepCopy(items);
    preAddLogic?.(newItem);
    newItems.push(newItem);
    onChange(newItems);
    setNewItem(getEmptyItem);
  };

  const deleteListItem = (index: number) => {
    let newItems = deepCopy(items);
    newItems.splice(index, 1);
    onChange(newItems);
  };
  
  const startBulkEditing = () => {
    setBulkEditValue(items.map(item => bulkEditFields.map(k => item[k]).join("|")).join("\n"));
    setIsBulkEditing(true);
  };

  const stopBulkEditing = (save: boolean) => {
    if (save) {
      onChange(bulkEditValue.split("\n").map(line => {
        let split = line.split("|");
        return split.reduce((acc, val, i) => {
          acc[bulkEditFields[i]] = val;
          return acc;
          }, {} as any);
      }));
    }

    setBulkEditValue("");
    setIsBulkEditing(false);
  };

  let children: ReactNode;
  if (isBulkEditing) {
    children = <>
      <TextField
        variant="standard"
        multiline
        rows={10}
        fullWidth
        maxRows={15}
        value={bulkEditValue}
        onChange={(e) => setBulkEditValue(e.target.value)}
      />
      <div className="bottom-buttons">
        <Button onClick={() => stopBulkEditing(true)}>Save</Button>
        <Button onClick={() => stopBulkEditing(false)}>Cancel</Button>
      </div>
    </>;
  } else {
    const onEditChanged = (item: T, setItem: (val: T) => void) => (prop, val) => {
      item = deepCopy(item);
      setObjectValue(item, prop, val);
      setItem(item);
    };

    let reorderItems: ReorderableItem[] = items.map((item, i) => {
      let node = <></>;

      if (i === listEditIndex) {
        node = <>
          <ItemEditor
            fields={fields}
            item={editItem}
            onChange={onEditChanged(editItem, setEditItem)}
            onEnterPressed={() => stopEditListItem(true)}
          />
          <div className="bottom-buttons">
            <Button onClick={() => stopEditListItem(true)}>Save</Button>
            <Button onClick={() => stopEditListItem(false)}>Cancel</Button>
          </div>
        </>;
      } else {
        if (stackEditDelete) {
          node = <>
            <div className="question-list-item-stack-edit-delete">
              <IconButton icon="reorder" className={reorderHandleClassname} />
              <IconButton icon="edit" onClick={() => startEditListItem(i)} />
              <IconButton icon="delete" onClick={() => deleteListItem(i)} />
            </div>
            <span>{displayItem(item)}</span>
          </>;
        } else {
          node = <>
            <IconButton icon="reorder" className={reorderHandleClassname} />
            <span>{displayItem(item)}</span>
            <IconButton icon="edit" onClick={() => startEditListItem(i)} />
            <IconButton icon="delete" onClick={() => deleteListItem(i)} />
          </>;
        }
      }

      return {
        id: `${i}`,
        node: <div className={classes("question-list-item", i === listEditIndex && "editing")}>{node}</div>,
      };
    });

    // make sure all required fields are filled out
    let blockAdd = fields.some(f => f.required && !(newItem[f.property] as string)?.trim());

    // if no fields are required, then just make sure one field has a value to avoid completely empty entries
    if (fields.every(f => !f.required)) {
      blockAdd = fields.every(f => !newItem[f.property]);
    }

    children = <>
      <ReorderableGrid
        items={reorderItems}
        onReorder={newOrder => onChange(newOrder.map(i => items[i]))}
        handleSelector={`.${reorderHandleClassname}`}
      />
      <ItemEditor
        fields={fields}
        item={newItem}
        onChange={onEditChanged(newItem, setNewItem)}
        isAdding
        onEnterPressed={addNewListItem}
      />
      <div className="bottom-buttons">
        {bulkEditFields && <Button onClick={startBulkEditing}>Bulk Edit</Button>}
        <Button onClick={addNewListItem} disabled={blockAdd}>Add</Button>
      </div>
    </>;
  }

  const className = "enumeration-item-editor";

  if (noCard) return <div className={className}>{children}</div>;

  return (
    <MCard title={title} cardContentClassName={className}>
      {children}
    </MCard>
  );
};

interface ItemEditorProps<T> {
  fields: EnumerationEditorField<T>[],
  isAdding?: boolean,
  item: any,
  onChange: (property: keyof T, newValue: string) => void,
  onEnterPressed?: () => void,
};

const ItemEditor = <T,>({ fields, item, onChange, isAdding, onEnterPressed }: ItemEditorProps<T>) => {
  const dialog = useContext<DialogProviderValue>(DialogContext);

  const onChangeWrapped = (field: EnumerationEditorField<T>) => (newValue: string) => {
    // @ts-ignore i promise replace all exists
    if (field.blockCommas) newValue = newValue.replaceAll(",", "");
    onChange(field.property, newValue);
  };

  const showExpressionEditorLocal = (field: EnumerationEditorField<T>) =>
    showExpressionEditor(dialog, undefined, item[field.property] as string, onChangeWrapped(field));

  return fields.filter(f => !isAdding || !f.hideOnAdd).map(f => {
    let control: ReactNode;

    if (f.options) {
      control = (
        <>
          {f.label && <Typography variant="body2">{f.label}</Typography>}
          <MSelect
            options={f.options}
            value={item[f.property]}
            onChange={(option: MSelectOption) => onChange(f.property, option.value as string)}
            isClearable={false}
          />
        </>
      );
    } else {
      const onKeyDown = event => {
        if (event.key === "Enter") onEnterPressed?.();
      };
      
      control = (
        <TextField
          variant="standard"
          label={f.label}
          onChange={(e) => onChangeWrapped(f)(e.target.value)}
          value={item[f.property]}
          onKeyDown={onKeyDown}
        />
      );
    }

    return <div className={classes("enumeration-editor-property-editor", f.options && "enumeration-options")}>
      {control}
      {f.isExpression && <IconButton icon="code" onClick={() => showExpressionEditorLocal(f)} />}
    </div>
  });
};

export default FormEnumerationEditor;